/**
 * Sticky project-level KPI bar for the Finance tab.
 *
 * Five KPIs from `projectSummary`: total turnover (emphasised on a
 * dark card per the design guide's "dark card treatment"), total
 * initial offer, hours sold, hours spent, % hours completion.
 *
 * Sticky offsets: 0 on desktop (no fixed top bar), below the 56px
 * MobileTopBar on mobile.
 */

import { Box, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MOBILE_TOP_BAR_HEIGHT } from '../../layout/MobileTopBar';
import { colors } from '../../core/theme/theme';
import {
  formatCurrencyEUR,
  formatNumber,
  type ProjectFinanceSummary,
} from './finance';

export default function FinanceKpiBar({
  summary,
  locale,
}: {
  summary: ProjectFinanceSummary;
  locale: string;
}) {
  const { t } = useTranslation();
  const items = [
    {
      key: 'totalTurnover',
      label: t('finance.summary.totalTurnover'),
      value: formatCurrencyEUR(summary.totalTurnover, locale),
      dark: true,
    },
    {
      key: 'totalInitialOffer',
      label: t('finance.summary.totalInitialOffer'),
      value: formatCurrencyEUR(summary.totalInitialOffer, locale),
      dark: false,
    },
    {
      key: 'totalHoursSold',
      label: t('finance.summary.totalHoursSold'),
      value: formatNumber(summary.totalHoursSold, locale),
      dark: false,
    },
    {
      key: 'totalHoursSpent',
      label: t('finance.summary.totalHoursSpent'),
      value: formatNumber(summary.totalHoursSpent, locale),
      dark: false,
    },
    {
      key: 'percentCompletion',
      label: t('finance.summary.percentCompletion'),
      value: `${formatNumber(summary.percentCompletion, locale)}%`,
      dark: false,
    },
  ];
  return (
    <Box
      sx={{
        position: 'sticky',
        top: { xs: MOBILE_TOP_BAR_HEIGHT, md: 0 },
        zIndex: 10,
        bgcolor: colors.white,
        py: 1,
      }}
      data-testid="finance-summary"
    >
      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
        {items.map((it) => (
          <Paper
            key={it.key}
            variant="outlined"
            sx={{
              px: 2,
              py: 1.25,
              minWidth: { xs: 110, sm: 130, md: 150 },
              flex: '1 1 130px',
              borderColor: it.dark ? colors.darkBg : colors.lightGray,
              bgcolor: it.dark ? colors.darkBg : colors.white,
            }}
            data-testid={`finance-summary-${it.key}`}
          >
            <Typography
              variant="caption"
              sx={{ color: it.dark ? colors.lightGray : colors.textMuted }}
            >
              {it.label}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                fontSize: 22,
                lineHeight: 1.2,
                color: it.dark ? colors.yellow : colors.bodyText,
              }}
            >
              {it.value}
            </Typography>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}
